import React, { useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Package, 
  Library, 
  FolderOpen, 
  Palette, 
  HelpCircle, 
  Settings,
  LogOut,
  Menu,
  X,
  ChevronRight,
  Plus,
  Shield
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useSessionMonitor } from '../../hooks/useSessionMonitor';
import Button from '../Button';

interface NavItem {
  name: string;
  icon: React.ReactNode;
  path: string;
  badge?: string;
}

const DashboardLayout: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, profile, signOut } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  // Monitor session health
  useSessionMonitor();

  const isAdmin = profile?.role === 'admin' || profile?.role === 'super_admin';

  const navItems: NavItem[] = [
    {
      name: 'Dashboard',
      icon: <LayoutDashboard className="w-5 h-5" />,
      path: '/dashboard'
    },
    {
      name: 'Templates',
      icon: <Package className="w-5 h-5" />,
      path: '/dashboard/mockups'
    },
    {
      name: 'Library',
      icon: <Library className="w-5 h-5" />,
      path: '/dashboard/library'
    },
    {
      name: 'Assets',
      icon: <FolderOpen className="w-5 h-5" />,
      path: '/dashboard/assets'
    },
    {
      name: 'Brand',
      icon: <Palette className="w-5 h-5" />,
      path: '/dashboard/brand'
    },
    {
      name: 'Help & Support',
      icon: <HelpCircle className="w-5 h-5" />,
      path: '/dashboard/support'
    },
    {
      name: 'User Settings',
      icon: <Settings className="w-5 h-5" />,
      path: '/dashboard/settings'
    },
    ...(isAdmin ? [{
      name: 'Admin Panel',
      icon: <Shield className="w-5 h-5" />,
      path: '/admin',
      badge: 'Admin'
    }] : [])
  ];

  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const isActivePath = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Sidebar - Desktop */}
      <div className="hidden md:flex md:w-64 md:flex-col">
        <div className="flex flex-col flex-grow pt-5 pb-4 overflow-y-auto bg-white border-r border-gray-200">
          {/* Logo */}
          <div className="flex items-center flex-shrink-0 px-4">
            <img 
              src="/brandscapes_ai_logo_small.png" 
              alt="BrandScapes AI" 
              className="h-10 w-auto"
            />
          </div>

          {/* Create Mockup Button */}
          <div className="px-4 mt-6">
            <Button
              to="/dashboard/create"
              variant="primary"
              size="medium"
              className="w-full"
            >
              <Plus className="w-5 h-5 mr-2" />
              Create New Mockup
            </Button>
          </div>

          {/* Navigation */}
          <nav className="mt-6 flex-1 px-3">
            <ul className="space-y-1">
              {navItems.map((item) => (
                <li key={item.path}>
                  <Link
                    to={item.path}
                    className={`
                      group flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-all
                      ${isActivePath(item.path)
                        ? 'bg-primary-50 text-brand-purple'
                        : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                      }
                    `}
                  >
                    <span className={`
                      ${isActivePath(item.path) ? 'text-brand-purple' : 'text-gray-400 group-hover:text-gray-500'}
                    `}>
                      {item.icon}
                    </span>
                    <span className="ml-3 flex-1">{item.name}</span>
                    {item.badge && (
                      <span className="ml-auto inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-brand-purple text-white">
                        {item.badge}
                      </span>
                    )}
                    {isActivePath(item.path) && (
                      <ChevronRight className="ml-auto w-4 h-4 text-brand-purple" />
                    )}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          {/* User Section */}
          <div className="flex-shrink-0 px-4 py-4 border-t border-gray-200">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 rounded-full bg-brand-purple text-white flex items-center justify-center text-sm font-medium">
                  {(profile?.full_name || (user as any)?.user_metadata?.full_name || (user as any)?.user_metadata?.name || user?.email || 'U')
                    .toString().charAt(0).toUpperCase()}
                </div>
              </div>
              <div className="ml-3 flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {profile?.full_name || (user as any)?.user_metadata?.full_name || (user as any)?.user_metadata?.name || 'User'}
                </p>
                <p className="text-xs text-gray-500 truncate">
                  {user?.email || ''}
                </p>
              </div>
              <button
                onClick={handleLogout}
                className="ml-3 p-1.5 text-gray-400 hover:text-gray-500 transition-colors"
                title="Logout"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile menu button */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-40 bg-white border-b border-gray-200">
        <div className="flex items-center justify-between px-4 h-16">
          <img 
            src="/brandscapes_ai_logo_small.png" 
            alt="BrandScapes AI" 
            className="h-8 w-auto"
          />
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100"
          >
            {isMobileMenuOpen ? (
              <X className="w-6 h-6" />
            ) : (
              <Menu className="w-6 h-6" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden fixed inset-0 z-30 bg-gray-600 bg-opacity-75" onClick={() => setIsMobileMenuOpen(false)}>
          <div className="fixed inset-y-0 left-0 max-w-xs w-full bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="pt-20 pb-4 overflow-y-auto">
              <nav className="px-3">
                <ul className="space-y-1">
                  {navItems.map((item) => (
                    <li key={item.path}>
                      <Link
                        to={item.path}
                        onClick={() => setIsMobileMenuOpen(false)}
                        className={`
                          group flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-all
                          ${isActivePath(item.path)
                            ? 'bg-primary-50 text-brand-purple'
                            : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                          }
                        `}
                      >
                        <span className={`
                          ${isActivePath(item.path) ? 'text-brand-purple' : 'text-gray-400 group-hover:text-gray-500'}
                        `}>
                          {item.icon}
                        </span>
                        <span className="ml-3 flex-1">{item.name}</span>
                        {item.badge && (
                          <span className="ml-auto inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-brand-purple text-white">
                            {item.badge}
                          </span>
                        )}
                      </Link>
                    </li>
                  ))}
                </ul>
              </nav>

              {/* User Section - Mobile */}
              <div className="mt-8 px-4 py-4 border-t border-gray-200">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 rounded-full bg-brand-purple text-white flex items-center justify-center text-sm font-medium">
                      {(profile?.full_name || (user as any)?.user_metadata?.full_name || (user as any)?.user_metadata?.name || user?.email || 'U')
                        .toString().charAt(0).toUpperCase()}
                    </div>
                  </div>
                  <div className="ml-3 flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {profile?.full_name || (user as any)?.user_metadata?.full_name || (user as any)?.user_metadata?.name || 'User'}
                    </p>
                    <p className="text-xs text-gray-500 truncate">
                      {user?.email || ''}
                    </p>
                  </div>
                  <button
                    onClick={handleLogout}
                    className="ml-3 p-1.5 text-gray-400 hover:text-gray-500 transition-colors"
                    title="Logout"
                  >
                    <LogOut className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <main className="flex-1 overflow-y-auto bg-gray-50 pt-16 md:pt-0">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
